import React from 'react';
import { adminStats, recentUsers, voiceAgents } from '../../data/adminMock';
import {
  Users,
  Bot,
  Phone,
  DollarSign,
  TrendingUp,
  Clock,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, change, changeType }) => (
  <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-teal-400/30 transition-all">
    <div className="flex items-start justify-between mb-4">
      <div className="w-12 h-12 bg-teal-400/10 rounded-lg flex items-center justify-center">
        <Icon className="w-6 h-6 text-teal-400" />
      </div>
      {change && (
        <div className={`flex items-center gap-1 text-sm ${
          changeType === 'up' ? 'text-green-400' : 'text-red-400'
        }`}>
          {changeType === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          {change}%
        </div>
      )}
    </div>
    <p className="text-gray-400 text-sm mb-1">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const AdminDashboard = () => {
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-gray-400">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <StatCard
          icon={Users}
          label="Total Users"
          value={adminStats.totalUsers.toLocaleString()}
          change={12.5}
          changeType="up"
        />
        <StatCard
          icon={Bot}
          label="Active Agents"
          value={adminStats.activeAgents.toLocaleString()}
          change={8.2}
          changeType="up"
        />
        <StatCard
          icon={Phone}
          label="Total Calls"
          value={`${(adminStats.totalCalls / 1000000).toFixed(1)}M`}
          change={23.5}
          changeType="up"
        />
        <StatCard
          icon={DollarSign}
          label="Revenue"
          value={`$${(adminStats.revenue / 1000).toFixed(0)}K`}
          change={15.3}
          changeType="up"
        />
        <StatCard
          icon={TrendingUp}
          label="Growth Rate"
          value={`${adminStats.growthRate}%`}
          change={2.1}
          changeType="up"
        />
        <StatCard
          icon={Clock}
          label="Avg Call Duration"
          value={adminStats.avgCallDuration}
          change={1.8}
          changeType="down"
        />
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Recent Users</h2>
            <a href="/admin/users" className="text-teal-400 text-sm hover:underline">View All</a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-400 text-xs font-medium pb-3">USER</th>
                  <th className="text-left text-gray-400 text-xs font-medium pb-3">ROLE</th>
                  <th className="text-left text-gray-400 text-xs font-medium pb-3">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.slice(0, 5).map((user) => (
                  <tr key={user.id} className="border-b border-gray-800/50">
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-sm font-medium">
                          {user.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{user.name}</p>
                          <p className="text-gray-500 text-xs">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className="text-gray-300 text-sm">{user.role}</span>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.status === 'active' ? 'bg-green-400/10 text-green-400' :
                        user.status === 'pending' ? 'bg-yellow-400/10 text-yellow-400' :
                        'bg-red-400/10 text-red-400'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Voice Agents */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Top Voice Agents</h2>
            <a href="/admin/agents" className="text-teal-400 text-sm hover:underline">View All</a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-400 text-xs font-medium pb-3">AGENT</th>
                  <th className="text-left text-gray-400 text-xs font-medium pb-3">CALLS</th>
                  <th className="text-left text-gray-400 text-xs font-medium pb-3">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {voiceAgents.slice(0, 5).map((agent) => (
                  <tr key={agent.id} className="border-b border-gray-800/50">
                    <td className="py-3">
                      <div>
                        <p className="text-white text-sm font-medium">{agent.name}</p>
                        <p className="text-gray-500 text-xs">{agent.owner}</p>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className="text-gray-300 text-sm">{agent.calls.toLocaleString()}</span>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        agent.status === 'active' ? 'bg-green-400/10 text-green-400' :
                        agent.status === 'paused' ? 'bg-yellow-400/10 text-yellow-400' :
                        'bg-gray-400/10 text-gray-400'
                      }`}>
                        {agent.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
